<template>
  <div
    v-if="!currentPageNotFound"
    class="header-wrap"
    :class="{ 'electron-drag': !isAnyCardDisplayedInModalLayout && !showPeekSidebar }"
  >
    <div
      v-if="showBoardWillNotShowOnPublicPageBanner"
      class="w-full bg-important-bg text-destructive-text rounded-t-lg border-0 border-b border-solid border-b-important-border text-center py-1 text-sm"
    >
      {{ translate('boardWillNotShowOnPublicPage') }}
    </div>
    <div
      ref="headerWrap"
      class="header-inner sidebar-collapsed"
      :class="[
        { 'sidebar-expanded': sidebarExpanded },
        { 'page-header': isPageOrNewPage },
        { 'rounded-t-lg': embed },
      ]"
    >
      <div
        ref="leftSide"
        class="header-column tabs"
        :style="{ maxWidth: columnMaxWidth }"
      >
        <SidebarToggle v-if="showToggle && !isInbox" />
        <EscTooltip
          v-if="isMobile && isPageOrNewPage && !isInbox"
          @closePopup="handleClose"
        />
        <Breadcrumbs
          v-if="!inEpicBoard"
          ref="headerBreadcrumbs"
          :type="breadcrumbsType"
          :resource-id="currentBoardId"
        />
        <AddFilter
          v-if="showFilterButton && !isMobile"
          :main-header="true"
        />
        <SearchBoard
          v-if="showSearchBoard"
          :key="currentBoardId"
        />
      </div>
      <div
        v-if="!isMobile && showSprintsDates"
        class="header-center flex grow-0 shrink-0"
      >
        <div
          v-if="isSprint && !displayProjectPreviewBoards"
          ref="sprintDates"
          class="flex items-center justify-center text-text-default text-sm font-normal"
        >
          {{ sprintDatePeriod }} ({{ sprintStatus }})
        </div>
      </div>
      <div
        ref="rightSide"
        class="header-column options"
      >
        <div
          v-if="
            pageUsageLimit > 0 && isPageOrNewPage && !embed && currentEditorUsagePercentage >= 90
          "
          :key="currentPageId"
          v-tooltip="{
            content: translate('pageUsageDescription'),
            placement: 'bottom',
          }"
          class="flex items-center justify-center hover:bg-activeItem-default cursor-pointer rounded text-xs font-normal text-deEmphasised-text px-1 py-[3px] mr-2"
        >
          <span class="mr-1">Page size</span>
          <CircularProgress
            :value="currentEditorUsagePercentage"
            :radius="8"
            :color-variation="circularProgressColor"
          />
          <span class="ml-1">{{ currentEditorUsagePercentage }}%</span>
        </div>
        <PublicPageButton v-if="isPageOrNewPage && isPublic && !embed" />
        <Toolbar
          v-if="isPage && !isMobile && !embed && !displayProjectPreviewBoards && !isGuestReader"
          :editor-id="editorId"
          :page-archived="isPageArchived"
        />
        <ButtonCustom
          v-if="showJoinButton && !isManageSpaces && !isCreateSpace && !inEpicBoard"
          v-tooltip="{
            content: translate('joinThisSpace'),
            placement: 'bottom',
          }"
          :text="translate('Join')"
          :type="'primary'"
          class="qc-button join-project-button"
          @click="joinProjectProcedure"
        />
        <div
          v-if="displayMembers"
          class="members-list-header-wrap"
          @click.stop="showInfoSidebar"
        >
          <CurrentModelMembers
            v-if="!isMobile && members.length"
            ref="members"
            v-tooltip="{
              content: `<span style='white-space: pre-line; line-height: 14px;'>${translate('viewMembers')}
              <strong>${members.length} ${translate('total')}</strong></span>`,
              onShow: onMembersTooltipShow,
              placement: 'bottom',
              allowHTML: true,
            }"
            class="members-list-header"
            :members="routeMembers"
            :are-project-members="true"
          />
          <div
            v-if="isMobile"
            class="add-member-icon-wrap electron-no-drag"
            :class="[{ 'bg-icon-bg': showBoardMembersDropdown || showProjectMembersDropdown }]"
            @click.stop="toogleMemberDropdown"
            @touchstart.prevent="toogleMemberDropdown"
          >
            <div
              v-if="displayMembers || isMobile"
              class="add-member-icon-content electron-no-drag"
            >
              <em
                class="custom-icons add-member-icon"
                :style="getCustomIconStyle(iconPath(addMemberIcon))"
              />
              <span
                v-if="isMobile"
                class="members-count"
              >
                {{ members.length }}
              </span>
            </div>
          </div>
        </div>
        <MemberDropdownWrap
          v-if="showBoardMembersDropdown || showProjectMembersDropdown"
          :entity="headerMemberEntity"
          :project-members="sortedMembersBasedOnRoute"
        />
        <CalendarHeaderOptions
          v-if="isCalendarView"
          class="electron-no-drag"
        />
        <div
          v-if="!displayCloseButton"
          class="options-buttons-wrap electron-no-drag"
          :class="{ 'left-border': showLeftBorder }"
        >
          <CardsLayoutOptionsButton
            v-if="showLayoutButton"
            :board-id="currentBoardId"
            :current-layout="currentLayout"
            @updateAttribute="updateAttribute"
          />
          <ButtonCustom
            v-if="inEpicBoard"
            :custom-icon-name="'plus'"
            :custom-icon-color="'bg-icon-default'"
            :type="'transparent'"
            class="rounded border-none hover:bg-icon-bg h-6"
            :text="translate('addNewCard')"
            @click="openQuickCard"
          />
          <ButtonCustom
            v-if="inEpicBoard"
            :type="'transparent'"
            :custom-icon-name="'search-alternative'"
            :custom-icon-color="'bg-icon-default'"
            class="rounded border-none hover:bg-icon-bg h-6"
            :text="translate('addExistingCard')"
            @click="openFindCard"
          />
          <WatchIcon
            v-if="
              (isPage || isBoard) &&
              !isMobile &&
              !embed &&
              !displayProjectPreviewBoards &&
              !isGuestReader
            "
            class="square-icon-wrap header-icon h-w-32"
            :resource-section="watchSection"
            :resource="watchResource"
            :archived="isPageArchived"
          />
          <BookmarkHeaderButton
            v-if="showFavouritesButton"
            :id="resourceId"
            class="ml-2 mr-0"
            :type="bookmarkHeaderButtonType"
          />
          <CommentPageButton
            v-if="showPageCommentButton"
            class="ml-2 mr-0"
          />
          <v-popover
            v-if="showMoreButton"
            interactive
            tag="div"
            content-tag="div"
            class="settings-button-popover square-icon-wrap h-w-32 header-icon usn overflow-hidden"
            :append-to="bodyRef"
            :class="[{ 'disabled-item': isNewBoard }]"
            :role="'popper'"
            :trigger="'click'"
            :on-show="() => !isNewBoard || !currentPageNotFound"
          >
            <template #default="{ state }">
              <SettingsButtonHomeHeader
                v-if="settingsDropdownItems"
                :class="{ 'bg-icon-bg': state.isVisible }"
              />
            </template>
            <template #content="{ state, hide }">
              <ListDropdown
                v-if="state.isVisible"
                :position="isProject ? 'project-dropdown' : 'page-board-dropdown'"
                :items="settingsDropdownItems"
                :width="250"
                :fixed-width="true"
                @click="handleSettingsDropdownClick($event, hide)"
                @hoverDropdownItem="handleSettingsDropdownHover"
                @closeEscape="hide"
                @closeDropdown="hide"
              />
            </template>
          </v-popover>
        </div>
        <v-popover
          v-if="displayCloseButton && !displayProjectPreviewBoards && !isMobile && !isBoard"
          :on-show="() => !draggingIsOn"
          class="flex electron-no-drag"
        >
          <ButtonCustom
            :type="'secondary-button white hidden-search hover:bg-hover-default'"
            :custom-icon-name="'x'"
            :text="translate('cancel')"
            class="!border !border-solid !border-important-border rounded"
            @click="handleClose"
          />
          <template #content>
            <TooltipTemplate
              :tooltip-text="translate('cancel')"
              :tooltip-meta="{ icon: { ctrl: false, key: 'ESC' } }"
            />
          </template>
        </v-popover>
        <SidebarHeaderButton
          v-if="displayToggleInfoSidebarButton"
          class="settings-button-popover square-icon-wrap h-w-32 header-icon usn"
        />
      </div>
    </div>
    <Portal
      v-if="isConfirmationRevealed"
      to="root-widget"
    >
      <DoubleCheck
        v-click-outside="cancelConfirmation"
        :member="$store.getters.getUser"
        :members-array="currentProject.members"
        :project="currentProject"
        :show-user-info="false"
        @closeDoubleCheckPopup="cancelConfirmation"
        @leaveSpaceConfirmed="fulfilConfirmation"
      />
    </Portal>
    <ItemNotFound v-if="itemNotFound" />
  </div>
</template>

<script>
import SettingsButtonHomeHeader from '@/components/elements/SettingsButtonHomeHeader';
import ListDropdown from '@/components/widgets/ListDropdown';
import CommentPageButton from '@/components/elements/CommentPageButton';
import CurrentModelMembers from '@/components/widgets/CurrentModelMembers';
import BookmarkHeaderButton from '@/components/elements/BookmarkHeaderButton';
import SidebarToggle from '@/components/widgets/SidebarToggle';
import useSetAndOpenProject from '@/utilities/composables/useSetAndOpenProject';
import MemberDropdownWrap from '@/components/widgets/MemberDropdownWrap';
import EventBus from '@/utilities/eventBus';
import Breadcrumbs from '@/components/widgets/Breadcrumbs';
import EscTooltip from '@/components/widgets/EscTooltip.vue';
import PublicPageButton from '@/components/elements/PublicPageButton';
import CalendarHeaderOptions from '@/boards/calendar/components/CalendarHeaderOptions.vue';
import getDashedTitle from '@/utilities/getDashedTitle';
import watchMixin from '@/mixins/watchMixin';
import {
  getPageBreadcrumbMaxWidth,
  getMobilePageBreadcrumbMaxWidth,
  getBoardHeaderRightSideWidth,
  getMobileBoardHeaderRightSideWidth,
  getSpaceHeaderRightSideWidth,
  getMobileSpaceHeaderRightSideWidth,
  getJoinProjectButtonWidth,
} from '@/utilities/rootStyles';
import AddFilter from '@/components/widgets/filters/AddFilter';
import customIconsMixin from '@/mixins/customIconsMixin';
import ButtonCustom from '@/components/elements/ButtonCustom';
import resourceActionsMixin from '@/mixins/resourceActionsMixin';
import CardsLayoutOptionsButton from '@/components/elements/cards/CardsLayoutOptionsButton';
import { getElementPositionByPointer } from '@/utilities/events/pointer';
import WatchIcon from '@/components/widgets/WatchIcon';
import BookmarkMixin from '@/mixins/BookmarkMixin';
import SidebarHeaderButton from '@/components/elements/SidebarHeaderButton.vue';
import Toolbar from '@/components/editor/toolbar/Toolbar';
import isCardMixin from '@/mixins/isCardMixin';
import DoubleCheck from '@/components/widgets/DoubleCheck.vue';
import { Portal } from 'portal-vue';
import { useConfirmDialog } from '@vueuse/core';
import {
  localUpdatePagePropForAllChildren,
  movePageChildrenToArchiveTree,
} from '@/utilities/localUpdatePageChildren';
import SearchBoard from '@/components/elements/SearchBoard.vue';

const pageBreadcrumbMaxWidth = getPageBreadcrumbMaxWidth();
const mobilePageBreadcrumbMaxWidth = getMobilePageBreadcrumbMaxWidth();
const boardHeaderRightSideWidth = getBoardHeaderRightSideWidth();
const mobileBoardHeaderRightSideWidth = getMobileBoardHeaderRightSideWidth();
const spaceHeaderRightSideWidth = getSpaceHeaderRightSideWidth();
const mobileSpaceHeaderRightSideWidth = getMobileSpaceHeaderRightSideWidth();
const joinProjectButtonWidth = getJoinProjectButtonWidth();
import sidebarMixin from '@/mixins/sidebarMixin';
import ItemNotFound from '@/components/widgets/ItemNotFound';
import TooltipTemplate from '@/components/elements/TooltipTemplate';
import { ResourceType, ResourceLoadedKey } from '@/types/resources';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import useOpenPage from '@/utilities/composables/useOpenPage';
import { convertTimestampToDate, getCurrentDateInSeconds } from '@/utilities/timeDifference';
import useToggleGoSquaredChatButton from '@/utilities/composables/useToggleGoSquaredChatButton';
import { globalState } from '@/utilities/globalState';
import useAddCardsToInEpicBoard from '@/utilities/composables/useAddCardsToInEpicBoard';
import useDuplicatePage from '@/utilities/composables/useDuplicatePage';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import CircularProgress from '@/components/elements/CircularProgress.vue';
import useUsageLimits from '@/utilities/composables/useUsageLimits';
import useEpicFeature from '@/utilities/composables/useEpicFeature';
import useBrowseArchivedResources from '@/utilities/composables/useBrowseArchivedResources';

export default {
  name: 'HeaderCustom',
  components: {
    SettingsButtonHomeHeader,
    ListDropdown,
    CommentPageButton,
    CurrentModelMembers,
    BookmarkHeaderButton,
    Breadcrumbs,
    SidebarToggle,
    MemberDropdownWrap,
    EscTooltip,
    PublicPageButton,
    CalendarHeaderOptions,
    AddFilter,
    ButtonCustom,
    CardsLayoutOptionsButton,
    WatchIcon,
    SidebarHeaderButton,
    Toolbar,
    DoubleCheck,
    Portal,
    ItemNotFound,
    TooltipTemplate,
    SearchBoard,
    CircularProgress,
  },
  mixins: [
    watchMixin,
    customIconsMixin,
    resourceActionsMixin,
    BookmarkMixin,
    isCardMixin,
    sidebarMixin,
  ],

  props: {
    currentLayout: {
      type: String,
      default: '',
    },

    resourceType: {
      type: String,
      default: '',
    },

    resourceId: {
      type: String,
      default: '',
    },
  },

  emits: ['updateAttribute'],

  setup(props) {
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
    const { setAndOpenAdjacentProject } = useSetAndOpenProject();
    const { isRevealed, reveal, confirm, cancel, onConfirm } = useConfirmDialog();
    const { openPage } = useOpenPage();
    const { isEventTargetGoSquaredInput } = useToggleGoSquaredChatButton();
    const { openQuickCard, openFindCard } = useAddCardsToInEpicBoard(props.resourceId);
    const { duplicatePage } = useDuplicatePage();
    const { isGuestReader } = useCurrentRole();
    const { pageUsageLimit } = useUsageLimits();
    const { isRoadmapWithoutFeatureFlag } = useEpicFeature();
    const { openArchivedCardsView } = useBrowseArchivedResources();

    return {
      isConfirmationRevealed: isRevealed,
      revealConfirmation: reveal,
      cancelConfirmation: cancel,
      onConfirmationDone: onConfirm,
      fulfilConfirmation: confirm,
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
      setAndOpenAdjacentProject,
      openPage,
      isEventTargetGoSquaredInput,
      openQuickCard,
      openFindCard,
      bodyRef: document.body,
      duplicatePage,
      isGuestReader,
      pageUsageLimit,
      isRoadmapWithoutFeatureFlag,
      openArchivedCardsView,
    };
  },
  data() {
    return {
      membersListAnimationDuration: 200,
      membersListAnimationFunction: 'linear',
      tabs: [
        {
          text: 'boards',
          icon: 'dashboard',
        },
        {
          text: 'pages',
          icon: 'insert_drive_file',
        },
      ],
      showJoinButton: false,
      listenersAdded: false,
      showSprintsDates: true,
      sprintDatesWidth: 0,
      cardsWithNoSprintButtonWidth: 0,
      headerRightSideWidth: 0,
      headerLeftSideWidth: 0,
      membersTooltipInstance: null,
    };
  },
  computed: {
    showBoardWillNotShowOnPublicPageBanner() {
      return this.isPageOrNewPage && this.isPublic && this.embed && !this.inEpicBoard;
    },
    isAnyCardDisplayedInModalLayout() {
      return this.$store.getters.getIsAnyCardDisplayedInLayout(this.$constants.modal);
    },
    isAnyCardDisplayed() {
      return this.$store.getters.getIsAnyCardDisplayed;
    },
    itemNotFound() {
      return this.$store.getters.getItemNotFound;
    },
    disableViewMembersTooltip() {
      return globalState.memberHoverPreviewShown;
    },
    showPeekSidebar() {
      return this.$store.getters.showPeekSidebar || false;
    },
    showToggle() {
      return (
        ((this.sidebarExpanded && this.showPeekSidebar) ||
          (!this.sidebarExpanded && !this.showPeekSidebar)) &&
        !this.inEpicBoard
      );
    },
    pageIndex() {
      return this.page?.public_settings?.robots?.includes('index');
    },
    projectNotEmpty() {
      return Object.keys(this.currentProject).length !== 0;
    },
    sidebarExpanded() {
      return this.$store.getters.sidebarExpanded;
    },
    currentProjectTitle() {
      return this.$store.getters.getCurrentProjectTitle;
    },
    currentBoardId() {
      if (this.embed) {
        return this.resourceId;
      }
      return this.$store.getters.currentBoardId;
    },
    currentBoard() {
      return this.$store.getters.getFlatBoards[this.currentBoardId] || {};
    },
    currentProjectId() {
      if (this.embed) {
        return this.currentBoard.project_id;
      }
      return this.$store.getters.getCurrentProjectId;
    },
    sprintDatePeriod() {
      return `${convertTimestampToDate(
        this.currentBoard.start_date
      )} - ${convertTimestampToDate(this.currentBoard.end_date)}`;
    },
    sprintStatus() {
      return this.translate(this.currentBoard.state);
    },
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
    getPreviousRoutePath() {
      return this.$store.getters.getPreviousRoutePath;
    },

    previousRouteName() {
      return this.$store.getters.getPreviousRouteName;
    },

    closeBoardRoute() {
      const projectPath = `${this.$cons.projectSlug}-${this.currentProjectId}`;
      return `/${this.teamSlug}/${projectPath}/boards`;
    },
    members() {
      return this.routeMembers || [];
    },
    currentProject() {
      return this.$store.getters.getCurrentProject;
    },
    currentProjectIsMine() {
      return Object.prototype.hasOwnProperty.call(this.currentProject, 'is_member');
    },
    isProjectOwner() {
      return this.currentProject.user_id === this.user.id;
    },
    settingsDropdownItems() {
      if (this.isProject) {
        const projectDropdownItems = [
          {
            id: 'copyLink',
            label: this.translate('copyLink'),
            customIconName: this.iconPath('link-st'),
          },
          {
            id: 'archivedCards',
            label: this.translate('browseArchivedCards'),
            customIconName: this.iconPath('archive'),
            ...(this.isGuestReader && { separator: true }),
          },
          ...(!this.isGuestReader
            ? [
                {
                  id: 'spaceSettings',
                  label: this.translate('spaceSettings'),
                  customIconName: this.iconPath('settings'),
                  ...(this.currentProjectId > 1 && !this.isGuestReader && { separator: true }),
                },
              ]
            : []),
        ];
        if (!this.currentProjectIsMine) {
          projectDropdownItems.push({
            id: 'joinProject',
            label: this.translate('joinSpace'),
            customIconName: this.iconPath('user-plus'),
          });
          return projectDropdownItems;
        } else {
          if (!this.isGuestReader) {
            projectDropdownItems.push({
              id: 'leaveProject',
              label: this.translate('leaveSpace'),
              customIconName: this.iconPath('log-out'),
            });
          }
          if (this.isOwner || this.isAdmin || this.isMyProject(this.currentProject.user_id)) {
            projectDropdownItems.push({
              id: 'delete',
              label: this.translate('deleteSpace'),
              customIconName: this.iconPath('trash'),
              deleteOrCancelType: true,
            });
          }
          return projectDropdownItems;
        }
      }
      if (this.isSprint) {
        const sprintItems = [
          {
            id: 'copyLink',
            label: this.translate('copyLink'),
            customIconName: this.iconPath('link-st'),
          },
          ...(!this.isGuestReader
            ? [
                {
                  id: 'sprintBoardSettings',
                  label: this.translate('boardSettings'),
                  customIconName: this.iconPath('settings'),
                },
              ]
            : []),
        ];

        return sprintItems;
      }
      if (this.isBoard || this.notificationTypeBoard || this.embed || this.isSprint) {
        const boardDropdown = [];
        if (this.isBoardDeleted) {
          boardDropdown.push({
            id: 'removeBoard',
            label: this.translate('removeBoard'),
            customIconName: this.iconPath('trash'),
          });
        } else {
          if (this.embed) {
            boardDropdown.push(
              {
                id: 'readOnly',
                label: this.translate('readOnly'),
                customIconName: this.iconPath('eye'),
                value: this.embeddedBoardAttributes.readOnly ?? false,
                toggleButton: true,
              },
              {
                id: 'makeBoardFullWidth',
                label: this.translate('makeBoardFullWidth'),
                customIconName: this.iconPath('expand-horizontal'),
                toggleButton: true,
                value: this.embeddedBoardAttributes.fullWidth ?? false,
                separator: true,
              }
            );
          }
          boardDropdown.push(
            {
              id: 'favourite',
              label: this.favouriteTitle,
              customIconName: this.iconPath(this.favouriteIcon),
            },
            {
              id: 'copyLink',
              label: this.translate('copyLink'),
              customIconName: this.iconPath('link-st'),
            },
            {
              id: 'watchBoard',
              label: this.watchLabel,
              customIconName: this.watchIcon,
            },
            {
              id: 'rename',
              label: this.translate('rename'),
              customIconName: this.iconPath('edit'),
            },
            {
              id: 'duplicateBoard',
              label: this.translate('duplicateBoard'),
              customIconName: this.iconPath('copy'),
            },
            {
              id: 'archivedCards',
              label: this.translate('browseArchivedCards'),
              customIconName: this.iconPath('archive'),
            },
            {
              id: 'settingsPage',
              label: this.translate('boardSettings'),
              customIconName: this.iconPath('settings'),
              ...(!this.embed && { separator: true }),
            },
            ...(this.embed
              ? [
                  {
                    id: 'removeBoard',
                    label: this.translate('removeBoard'),
                    customIconName: this.iconPath('trash'),
                  },
                ]
              : [])
          );
          if (!this.currentBoard.archived && !this.embed) {
            boardDropdown.push(
              {
                id: 'archiveBoard',
                label: this.translate('archiveResource', {
                  type: this.translate(ResourceType.Board).toLowerCase(),
                }),
                customIconName: this.iconPath('archived'),
              },
              {
                id: 'deleteBoard',
                label: this.translate('deleteResource', {
                  type: this.translate(ResourceType.Board).toLowerCase(),
                }),
                customIconName: this.iconPath('trash'),
                deleteOrCancelType: true,
              }
            );
          }
        }
        if (this.isGuestReader) {
          return boardDropdown.filter((item) => ['favourite', 'copyLink'].includes(item.id));
        }
        return boardDropdown;
      }
      if ((this.isPage && !this.isPageArchived) || this.notificationTypePage) {
        const dashedPageTitle = getDashedTitle(this.page.title);
        const publicUrl = this.page.public_settings?.url
          ? `${this.page.public_settings?.url}-${dashedPageTitle}`
          : '';
        const pageDropdown = [
          {
            id: 'info',
            label: this.translate('info'),
            customIconName: this.iconPath('info'),
          },
          {
            id: 'watchPage',
            label: this.watchLabel,
            customIconName: this.watchIcon,
          },
          {
            id: 'favourite',
            label: this.favouriteTitle,
            customIconName: this.iconPath(this.favouriteIcon),
          },
          {
            id: 'duplicatePage',
            label: this.translate('duplicate'),
            customIconName: this.iconPath('copy'),
          },
          {
            id: 'movePageTo',
            label: this.translate('moveTo'),
            customIconName: this.iconPath('arrow-right'),
          },
          {
            id: 'print',
            label: this.translate('print'),
            customIconName: this.iconPath('printer'),
            separator: true,
            hover: true,
          },
          {
            id: 'makePagePublic',
            value: this.page.is_public,
            pageUrl: publicUrl,
            label: this.translate('shareToWeb'),
            customIconName: this.iconPath('globe'),
            separator: !this.isPublic,
            toggleButton: true,
            publicPageItem: true,
          },
        ];
        pageDropdown.push(
          {
            id: 'hideSubpages',
            label: this.translate('showSubpages'),
            customIconName: this.iconPath('page'),
            toggleButton: true,
            value: !this.page?.hide_subpages ?? true,
          },
          {
            id: 'hideTableOfContents',
            label: this.translate('tableOfContents'),
            customIconName: this.iconPath('list'),
            separator: true,
            toggleButton: true,
            value: !this.page?.hide_table_of_contents ?? true,
          }
        );
        pageDropdown.push(
          {
            id: 'archivePage',
            label: this.translate('archiveResource', {
              type: this.translate(ResourceType.Page).toLowerCase(),
            }),
            customIconName: this.iconPath('archive'),
          },
          {
            id: 'deletePage',
            label: this.translate('deleteResource', {
              type: this.translate(ResourceType.Page).toLowerCase(),
            }),
            customIconName: this.iconPath('trash'),
            deleteOrCancelType: true,
          }
        );

        if (this.isPublic) {
          const indexOfMakePublicSection = pageDropdown.findIndex(
            (section) => section.id === 'makePagePublic'
          );

          if (indexOfMakePublicSection > -1) {
            pageDropdown.splice(indexOfMakePublicSection + 1, 0, {
              id: 'indexToggle',
              label: this.translate('searchEngineIndexing'),
              separator: true,
              toggleButton: true,
              value: this.pageIndex,
              publicPageIndex: true,
              disabled: !this.$store.getters.getTeamFeatureEnabled(
                this.$constants.featureFlagIds.publicPageIndexing
              ),
            });
          }
        }
        if (this.isGuestReader) {
          return pageDropdown.filter((item) => item.id === 'info');
        }
        return pageDropdown;
      }
      if (this.isSprints && !this.isGuestReader) {
        return [
          {
            id: 'addSprint',
            label: this.translate('addSprint'),
            customIconName: this.iconPath('plus'),
          },
          {
            id: 'sprintSettings',
            label: this.translate('sprintSettings'),
            customIconName: this.iconPath('settings'),
          },
        ];
      }
      return [];
    },
    page() {
      return this.$store.getters.getPage;
    },
    lightBackground() {
      return this.$store.getters.getBoardsBackground === 'light';
    },
    darkBackground() {
      return this.$store.getters.getBoardsBackground === 'dark';
    },
    showBackgroundsForSelect() {
      return this.$store.getters.getBoardsBackground !== '';
    },
    isNewBoard() {
      return this.$route.name === 'newboard';
    },
    boardDropdownsOpened() {
      return (
        this.$store.getters.getCurrentCardIdActionEdit !== '' ||
        this.$store.getters.getCurrentCardIdMemberDropdown !== ''
      );
    },
    isUploadImagePopupActive() {
      return (
        this.$store.getters.showImageUploadPopup || this.$store.getters.showCoverImageUploadPopup
      );
    },
    isSearchModalActive() {
      return this.$store.getters.showSearchModal;
    },
    displayCloseButton() {
      return this.isNewPage;
    },
    displayMembers() {
      return (
        (![
          'archived',
          'visited',
          'edited',
          'bookmarked',
          'page',
          'board',
          'newboard',
          'inbox',
          'sprint',
          'sprints',
          this.$constants.manageSpacesRouteName,
          this.$constants.createSpaceRouteName,
        ].includes(this.$route.name) &&
          !this.isNewBoard &&
          !this.isNewPage &&
          !this.inEpicBoard) ||
        this.displayProjectPreviewBoards
      );
    },
    columnMaxWidth() {
      // reduce width of space/board names when join button is present
      const joinButtonMargins = 24;
      const joinButtonWidth = this.showJoinButton ? joinProjectButtonWidth + joinButtonMargins : 0;
      if (this.$route.name === 'page')
        return this.isMobile ? `${mobilePageBreadcrumbMaxWidth}%` : `${pageBreadcrumbMaxWidth}%`;
      if (this.$route.name === 'board') {
        const headerRightSideWidth = this.isMobile
          ? mobileBoardHeaderRightSideWidth
          : boardHeaderRightSideWidth;
        return `calc(100% - ${headerRightSideWidth}px - ${joinButtonWidth}px)`;
      }
      if (this.$route.name === 'boards') {
        const headerRightSideWidth = this.isMobile
          ? mobileSpaceHeaderRightSideWidth
          : spaceHeaderRightSideWidth;
        return `calc(100% - ${headerRightSideWidth}px - ${joinButtonWidth}px)`;
      }
      return 'noset';
    },
    showInsertLinkPopup() {
      return this.$store.getters.showInsertLinkPopup;
    },
    currentMembers() {
      return this.$store.getters.getCurrentMembers;
    },
    user() {
      return this.$store.getters.getUser;
    },
    displayProjectPreviewBoards() {
      return this.$store.getters.displayProjectPreviewBoards;
    },
    inactiveMembers() {
      return this.$store.getters.getInactiveTeamMembers;
    },
    previewProject() {
      return this.$store.getters.previewProject ?? {};
    },
    activePreviewProjectMembers() {
      return this.filterInactiveMembers(this.previewProject.members);
    },
    routeMembers() {
      if (this.$store.getters.displayProjectPreviewBoards) {
        return this.sortMembersByAssignedDate(this.activePreviewProjectMembers);
      }
      if (this.isAnyCardDisplayed) {
        return this.sortMembersByAssignedDate(this.filterInactiveMembers(this.currentMembers));
      }
      return this.sortedMembersBasedOnRoute;
    },
    sortedMembersBasedOnRoute() {
      return this.sortMembersByAssignedDate(this.membersBasedOnRoute);
    },
    showLeftBorder() {
      return (this.isProject || this.displayProjectPreviewBoards) && !this.inEpicBoard;
    },
    showMoreButton() {
      return (
        !this.inEpicBoard &&
        (this.settingsDropdownItems.length > 0 || this.isNewBoard || this.isInbox)
      );
    },
    breadcrumbsType() {
      if (this.embed) {
        return this.resourceType;
      }
      if (this.displayProjectPreviewBoards) return 'boards';

      switch (this.$route.name) {
        case 'boards':
          return 'boards';
        case 'pages':
          return 'pages';
        case 'board':
          return 'board';
        case 'page':
        case 'newpage':
          return 'page';
        case 'inbox':
          return this.notificationType;
        case 'newboard':
          return 'newboard';
        case this.$constants.manageSpacesRouteName:
          return this.$constants.manageSpacesRouteName;
        case this.$constants.createSpaceRouteName:
          return this.$constants.createSpaceRouteName;
        case this.$constants.epicsBoardRoute.name:
          return ResourceType.EpicsBoard;
        case this.$constants.routeNames.sprintsRouteName:
          return ResourceType.Sprints;
        case this.$constants.routeNames.sprint:
          return ResourceType.Sprint;
        default:
          return '';
      }
    },
    displayMembersDropdown() {
      return this.$store.getters.getDisplayMembersDropdownId;
    },
    addMemberPopupIsOn() {
      return this.$store.getters.getAddMemberPopupIsOn;
    },

    showBoardMembersDropdown() {
      return this.$store.getters.getCurrentCardIdMemberDropdown === 'board';
    },
    showProjectMembersDropdown() {
      return (
        this.$store.getters.getCurrentCardIdMemberDropdown === 'project' &&
        this.$store.getters.getAddMembersTarget.type === 'project'
      );
    },
    headerMemberEntity() {
      if (this.isBoard) return this.currentBoard;
      if (this.isProject) return this.currentProject;
      return {};
    },
    pageWatch() {
      return this.page.is_watching;
    },
    boardWatch() {
      return this.currentBoard.is_watching;
    },
    watchResource() {
      if (this.isPage && !this.embed) {
        return this.page;
      } else if (
        this.isBoard ||
        (this.embed && this.resourceType === ResourceType.Board) ||
        this.isSprint
      ) {
        return this.currentBoard;
      }

      return null;
    },
    watchState() {
      return this.watchResource?.is_watching;
    },
    watchLabel() {
      return this.watchState ? this.translate('unsubscribe') : this.translate('subscribe');
    },
    watchIcon() {
      return this.watchState ? this.iconPath('bell-off') : this.iconPath('bell');
    },
    watchSection() {
      if (this.isPage) {
        return ResourceType.Page;
      } else if (this.isBoard) {
        return ResourceType.Board;
      }

      return '';
    },

    isPublic() {
      return !!this.page?.is_public;
    },

    isCalendarView() {
      return (
        this.$store.getters.cardsLayout(this.currentBoardId) === this.$constants.calendarLayout
      );
    },

    commentPopupVisible() {
      return this.$store.getters.commentPopupVisible;
    },

    pageCreating() {
      return this.$store.getters.pageCreating;
    },

    linkPopupIsActive() {
      return this.$store.getters.getDisplayEditLinkPopup;
    },

    displayInfoSidebar() {
      return this.$store.getters.displayInfoSidebar;
    },

    newPageCreating() {
      return this.$store.getters.getNewPageCreating;
    },

    pageEditorKey() {
      return this.newPageCreating
        ? 'newpage'
        : this.page.id || this.$store.getters.getCurrentPageID;
    },

    currentEditor() {
      return this.$store.getters.editor(this.editorId);
    },

    currentEditorUsagePercentage() {
      return this.currentEditor?.currentEditorUsagePercentage.value >= 0
        ? this.currentEditor?.currentEditorUsagePercentage.value
        : null;
    },

    circularProgressColor() {
      switch (true) {
        case this.currentEditorUsagePercentage > 70 && this.currentEditorUsagePercentage < 90:
          return 'orange';
        case this.currentEditorUsagePercentage >= 90:
          return 'red';
        default:
          return 'blue';
      }
    },

    sidebarDraftPage() {
      return this.$store.getters.sidebarDraftPage;
    },

    showFilterButton() {
      return (
        (this.isNewBoard ||
          this.isBoard ||
          this.isEpicsBoard ||
          this.isSprint ||
          this.inEpicBoard) &&
        !this.displayProjectPreviewBoards &&
        !this.isRoadmapWithoutFeatureFlag
      );
    },

    showSearchBoard() {
      return (
        (this.isBoard || this.isSprint || this.isEpicsBoard || this.inEpicBoard) &&
        !this.isRoadmapWithoutFeatureFlag
      );
    },

    showLayoutButton() {
      return (
        (this.isBoard ||
          this.isNewBoard ||
          this.notificationTypeBoard ||
          this.isEpicsBoard ||
          this.inEpicBoard ||
          this.embed ||
          this.isSprint) &&
        !this.isBoardDeleted &&
        !this.isRoadmapWithoutFeatureFlag
      );
    },

    childPagesSectionVisible() {
      return this.page?.hide_subpages ?? false;
    },

    editorKey() {
      return this.isPageOrNewPage ? this.pageEditorKey : '';
    },

    notificationType() {
      return this.$store.getters.getCurrentInboxResource.resource_type;
    },

    notificationTypePage() {
      return this.notificationType === 'page';
    },

    notificationTypeBoard() {
      return this.notificationType === 'board';
    },

    isPageOrNewPage() {
      return this.isPage || this.isNewPage || this.notificationTypePage;
    },

    isBookmarked() {
      if (this.embed) {
        return this.isFavourite(this.currentBoardId, this.resourceType);
      }
      return this.isPageOrNewPage
        ? this.isFavourite(this.page.id, 'page')
        : this.isFavourite(this.currentBoardId, 'board');
    },

    bookmarkHeaderButtonType() {
      if (this.isSprint) return ResourceType.Sprint;
      return this.isPageOrNewPage ? ResourceType.Page : ResourceType.Board;
    },

    favouriteTitle() {
      return this.isBookmarked ? this.translate('unfavourite') : this.translate('favourite');
    },

    favouriteIcon() {
      return this.isBookmarked ? 'unfavourite' : 'star';
    },

    showFavouritesButton() {
      return (
        (this.isPageOrNewPage ||
          this.isBoard ||
          this.isNewBoard ||
          this.isInbox ||
          this.isSprint) &&
        !this.displayProjectPreviewBoards &&
        !this.isMobile &&
        !this.embed
      );
    },

    editorId() {
      const teamId = this.$store.getters.getTeamID;
      if (this.isPage || this.isInbox) {
        return `${teamId}-editor-page-${this.$store.getters.getCurrentPageID}`;
      }
      if (this.isNewPage) {
        return `${teamId}-editor-page-newpage`;
      }
      return '';
    },

    addMemberIcon() {
      return this.isMobile ? 'user' : 'user-plus';
    },

    isPageArchived() {
      return this.page?.archived?.time_archived != null;
    },

    isManageSpaces() {
      return this.$route.name === this.$constants.manageSpacesRouteName;
    },

    isCreateSpace() {
      return this.$route.name === this.$constants.createSpaceRouteName;
    },

    displayToggleInfoSidebarButton() {
      return !this.inEpicBoard && (this.isBoard || this.isProject || this.notificationTypeBoard);
    },
    deletePopupIsOn() {
      return this.$store.getters.getDeletePopupId;
    },

    isConfirmationModalActive() {
      return this.$store.getters.getConfirmationModal;
    },

    currentPageId() {
      return this.$store.getters.getCurrentPageID;
    },

    projectPageTree() {
      return this.$store.getters.getProjectPagesTree(this.currentProjectId) || {};
    },

    pageFromTree() {
      const pageKey =
        this.sidebarDraftPage?.id && this.isNewPage
          ? this.sidebarDraftPage?.id
          : this.currentPageId;
      if (this.isPageArchived) {
        return this.projectPageArchivedTree[pageKey] ?? {};
      }
      return this.projectPageTree[pageKey] ?? {};
    },

    isChildPage() {
      return this.pageFromTree.parent !== 'root';
    },

    parentPage() {
      return this.isChildPage ? this.$store.getters.getPageById(this.pageFromTree.parent) : null;
    },

    projectPageArchivedTree() {
      return this.$store.getters.getProjectPagesArchiveTree(this.currentProjectId) || {};
    },

    sprintIds() {
      return Object.keys(this.$store.getters.getSpaceSprints(this.currentProjectId)) || [];
    },

    currentPageNotFound() {
      return (
        this.$store.getters.getCurrentPageMode === 'notfound' &&
        this.$route.name === ResourceType.Page
      );
    },

    flatBoards() {
      return this.$store.getters.getFlatBoards;
    },

    isBoardDeleted() {
      return this.flatBoards[`${this.currentBoardId}_notFound`];
    },

    showPageCommentButton() {
      return (
        (this.isPage || this.notificationTypePage) &&
        !this.embed &&
        !this.displayProjectPreviewBoards &&
        !this.isGuestReader
      );
    },

    upgradePlanPopUpIsDisplayed() {
      return !!this.$store.getters.getShowUpgradePlanPopup;
    },
  },

  watch: {
    '$route.name': {
      handler() {
        if (this.showBoardMembersDropdown || this.showProjectMembersDropdown) {
          this.closeMembersDropdown();
        }
      },
    },
    currentProject() {
      if (this.projectNotEmpty) {
        this.showJoinButton = !this.currentProjectIsMine;
      }
    },
  },

  mounted() {
    this.addEventListeners();
    this.$store
      .dispatch('fetchTeamMembers')
      .then(() => {
        this.$store.dispatch('setResourceLoaded', {
          resource: ResourceLoadedKey.TeamMembers,
          value: true,
        });
      })
      .catch(() => {});
    this.leftSideRo = new ResizeObserver(this.sideSectionsResize).observe(this.$refs.leftSide);
    this.rightSideRo = new ResizeObserver(this.sideSectionsResize).observe(this.$refs.rightSide);
    this.hideSprintDataObserver = new ResizeObserver(this.hideSprintHeaderElement).observe(
      this.$refs.headerWrap
    );
    if (this.projectNotEmpty) {
      this.showJoinButton = !this.currentProjectIsMine;
    }

    if (this.$route.name !== this.$constants.newPage) {
      this.$store.dispatch('setSidebarPageDraft', {});
    }
    this.$nextTick(() => {
      this.sprintDatesWidth = this.$refs.sprintDates?.offsetWidth || 0;
      this.cardsWithNoSprintButtonWidth =
        this.$refs.cardsWithNoSprintButtonRef?.$el.clientWidth || 0;
      this.headerLeftSideWidth = this.$refs.leftSide?.offsetWidth || 0;
      this.headerRightSideWidth = this.$refs.rightSide?.offsetWidth || 0;
    });
  },

  beforeUnmount() {
    this.removeEventListeners();
    this.leftSideRo?.disconnect();
    this.rightSideRo?.disconnect();
    this.hideSprintDataObserver?.disconnect();
  },

  activated() {
    this.addEventListeners();
  },

  deactivated() {
    this.removeEventListeners();
  },
  methods: {
    onMembersTooltipShow(instance) {
      this.membersTooltipInstance = instance;
      const disableViewMembersTooltipWatcher = this.$watch(
        'disableViewMembersTooltip',
        (newValue) => {
          if (newValue) {
            this.membersTooltipInstance?.hide?.();
            disableViewMembersTooltipWatcher();
          }
        }
      );
    },
    showInfoSidebar() {
      if (this.pageNotFound) return;
      if (!this.displayInfoSidebar) {
        this.$store.dispatch('setCommentsAreActive', false);
        this.$store.dispatch('setCurrentEditCommentID', '');
      }
      this.$store.dispatch('displayInfoSidebar', !this.displayInfoSidebar);
    },
    async handleSettingsDropdownClick(item, hideFn) {
      if (!item.toggleButton) hideFn?.();

      switch (item.id) {
        case 'makeBoardPublic':
          break;
        case 'leaveProject':
          await this.leaveProject();
          break;
        case 'archiveBoard':
          this.arhciveBoardProcedure();
          break;
        case 'settingsPage':
          this.$store.dispatch('setSettingsCurrentBoardId', this.currentBoardId);
          this.$router.push(
            `/${this.teamSlug}/settings/${this.$cons.projectSlug}` +
              `-${this.currentProjectId}/boards/${this.currentBoardId}`
          );
          break;
        case 'archivePage':
          this.setPagePrivate();
          this.archivePageProcedure();
          break;
        case 'setBoardsBackground':
          this.setBackgroundsActive();
          break;
        case 'joinProject':
          this.joinProjectProcedure();
          break;
        case 'watchPage':
          this.watchPage();
          break;
        case 'watchBoard':
          this.watchBoard();
          break;
        case 'rename':
          this.renameSectionTitle();
          break;
        case 'makePagePublic':
          this.togglePublicPage();
          break;
        case 'indexToggle':
          this.togglePageIndex();
          break;
        case 'duplicatePage':
          this.duplicatePage(this.page);
          break;
        case 'info':
          this.showInfoSidebar();
          break;
        case 'copyLink':
          this.generateLink(this.breadcrumbsType);
          break;
        case 'spaceSettings':
          this.$router.push(
            `/${this.teamSlug}/settings/${this.$cons.projectSlug}-${this.currentProjectId}`
          );
          break;
        case 'delete':
          this.openDeletePopup();
          break;
        case 'darkmode':
          this.$store.dispatch('toggleDarkMode');
          this.resetPreviewFont();
          break;
        case 'print':
          EventBus.$emit('beforePrint');
          window.print();
          break;
        case 'favourite':
          if (this.embed) {
            this.toggleBookmark(this.currentBoard, this.resourceType);
            break;
          }
          if (this.isPageOrNewPage) {
            this.toggleBookmark(this.page, ResourceType.Page);
          } else {
            this.toggleBookmark(this.board, ResourceType.Board);
          }
          break;
        case 'makeBoardFullWidth':
          this.$emit('updateAttribute', {
            [this.$constants.fullWidth]: !this.embeddedBoardAttributes?.fullWidth,
          });
          break;
        case 'readOnly':
          this.$emit('updateAttribute', {
            [this.$constants.readOnly]: !this.embeddedBoardAttributes?.readOnly,
          });
          break;
        case 'removeBoard':
          this.embeddedBoardAttributes.deleteNode();
          break;
        case 'duplicateBoard':
          EventBus.$emit('duplicateBoard', this.currentBoard.id, this.currentBoard.title);
          break;
        case 'sprintSettings':
          this.$router.push(
            `/${this.teamSlug}/settings/${this.$cons.projectSlug}-${this.currentProjectId}/sprints`
          );
          break;
        case 'sprintBoardSettings':
          this.$router.push({
            name: this.$constants.settingsSelectedBoardRouteName,
            params: {
              projectIdParam: this.currentProjectId,
              boardId: ResourceType.Sprints,
            },
          });
          break;
        case 'addSprint':
          this.$store.dispatch('createNewSprint', {
            project_id: this.currentProjectId,
          });
          break;
        case 'deletePage':
          this.$store.dispatch('deletePopupId', `${ResourceType.Page}-${this.page.id}`);
          break;
        case 'deleteBoard':
          this.$store.dispatch('deletePopupId', `${ResourceType.Board}-${this.currentBoardId}`);
          break;
        case 'movePageTo':
          this.$store.dispatch('setPageIdForMove', this.page.id);
          break;
        case 'hideTableOfContents':
        case 'hideSubpages':
          this.handleTableOfContentsAndSubpagesToggle(item);
          break;
        case 'archivedCards':
          const resourceId = this.isProject ? this.currentProjectId : this.currentBoardId;
          const resourceType = this.isProject ? ResourceType.Project : ResourceType.Board;
          this.openArchivedCardsView(resourceId, resourceType);
          break;
        default:
          break;
      }
    },
    handleSettingsDropdownHover(item) {
      if (item.id === 'print') {
        EventBus.$emit('beforePrint');
      }
    },
    openDeletePopup() {
      return this.$store.dispatch(
        'deletePopupId',
        `${ResourceType.Project}-${this.currentProjectId}`
      );
    },
    async leaveProject() {
      if (this.isProjectOwner && this.currentProject.members.length > 1) {
        EventBus.$emit('mustTransferSpaceOwner', this.currentProjectId);
        return;
      }

      const { isCanceled } = await this.revealConfirmation();
      if (isCanceled) return;

      const projectId = this.currentProjectId;
      const previousProjectName = this.currentProject.title;

      const teamProjects = [...this.$store.getters.getTeamProjects];
      const projectIndex = teamProjects.findIndex((p) => p.id === projectId);
      delete teamProjects[projectIndex].is_member;
      this.$store.dispatch('setTeamProjects', teamProjects);
      this.setAndOpenAdjacentProject();

      try {
        await this.$store.dispatch('removeMemberFromProject', {
          memberToRemove: this.findCurrentMembersById(this.user.id),
          projectId,
        });
        await this.$store.dispatch('addUiNotification', {
          message: this.translate('youLeftNamedSpace', { name: previousProjectName }),
          duration: 4000,
        });
      } catch {
        await this.$store.dispatch('addUiNotification', {
          message: this.translate('youCannotLeaveTheSpace'),
          status: this.$constants.uiNotificationStatuses.error,
          duration: 4000,
        });
      }
    },
    arhciveBoardProcedure() {
      this.$store.dispatch('localRemoveBoardFromOrder', {
        projectId: this.currentProjectId,
        boardId: this.currentBoardId,
      });

      this.$store.dispatch('removeBoardFromProjectBoards', this.currentBoard);

      this.$store.dispatch('archiveBoard', { id: this.currentBoardId, archived: true });
    },

    archivePageProcedure() {
      const pageForArchive = this.$store.getters.getProjectPagesTree(this.page.project_id)[
        this.page.id
      ];
      const pageChildren = pageForArchive?.children;

      if (pageForArchive && pageForArchive?.children && pageForArchive?.children.length > 0) {
        movePageChildrenToArchiveTree(pageChildren, this.page.project_id);
      }

      this.$store.dispatch('archivePage', {
        pageId: this.page.id,
        projectId: this.page.project_id,
      });
    },
    watchPage() {
      this.toggleWatch(this.pageWatch, ResourceType.Page, this.page.id);
    },
    watchBoard() {
      this.toggleWatch(this.boardWatch, ResourceType.Board, this.currentBoard.id);
    },
    closeOnClick($event) {
      if ($event.type === 'click') {
        if (this.isPage || this.isNewPage) {
          $event.stopPropagation();
          this.closeUploadPopupsInPage();
          this.closePage();
          this.clearSidebarDraftPage();
        } else if (this.isBoard || this.isNewBoard) {
          this.hideBoardDropdowns();
          this.closeBoard();
        }
      }
    },
    hideBoardDropdowns() {
      this.$store.dispatch('setCurrentCardIdForActionEdit', '');
      this.$store.dispatch('setCurrentCardIdMemberDropdown', '');
      this.$store.dispatch('setCurrentCardMembersDropdown', {});
      this.$store.dispatch('setHeaderMemberDropdownPosition', {});
    },
    handleQuickPageClose($event) {
      if (
        ($event.keyCode === 27 || $event.key === 'Escape') &&
        !this.isEventTargetGoSquaredInput($event)
      ) {
        this.$store.dispatch('setQuickPage', false);
      }
    },
    closeQuickResource(e) {
      if (this.upgradePlanPopUpIsDisplayed) return true;
      if (this.$store.getters.getQuickCardIsOn && !this.linkPopupIsActive) {
        this.handleQuickCardClose(e);
        return true;
      }
      if (this.$store.getters.getQuickPage && !this.linkPopupIsActive) {
        this.handleQuickPageClose(e);
        return true;
      }
      if (this.$store.getters.getQuickNote && !this.linkPopupIsActive) {
        this.handleQuickNoteClose(e);
        return true;
      }

      return false;
    },
    handleClose(e) {
      if (this.closeQuickResource(e)) return;

      if (this.isSearchModalActive) {
        // we pass esc handling to SearchModal
        return;
      }
      if (this.isConfirmationModalActive) {
        return;
      }
      if (!!this.$store.getters.getShowUpgradePlanPopup) {
        return;
      }
      if (e.keyCode === 27 || e.key === 'Escape') {
        if (this.isEventTargetGoSquaredInput(e)) {
          return;
        }
        if (!this.embed) {
          this.handleEscape(e);
        }
        this.hideBoardDropdowns();
      }
      this.closeOnClick(e);
    },
    closeUploadPopupsInPage() {
      this.$store.dispatch('setImageUploadPopup', false);
      this.$store.dispatch('setCoverImageUploadPopup', false);
      this.$store.dispatch('setPageIconUploadPopup', false);
      if (this.commentPopupVisible) {
        this.$store.dispatch('commentPopupVisible', false);
      }
    },
    handleQuickCardClose($event) {
      if ($event.keyCode === 27 || $event.key === 'Escape') {
        this.$store.dispatch('setQuickCard', false);
      }
    },
    handleQuickNoteClose($event) {
      if ($event.keyCode === 27 || $event.key === 'Escape') {
        this.$store.dispatch('setQuickNote', false);
      }
    },
    handleEscape($event) {
      if (this.deletePopupIsOn) return;

      if (
        this.draggingIsOn &&
        [ResourceType.Epic, ResourceType.Card].includes(this.draggedEntity.entityName)
      ) {
        this.$store.dispatch('setPressedEscCancelDrag', true);
        this.$store.dispatch('setDraggedEntity', {});
        this.$store.dispatch('setDropTargetId', '');
        this.$store.dispatch('setDraggingEnd', true);
        return;
      }

      if (this.$store.getters.getDoubleCheckPopup) {
        this.$store.dispatch('setDoubleCheckPopup', false);
        $event.stopPropagation();
        return;
      }
      if (this.$store.getters.getEmojiPickerDisplayed) {
        this.$store.dispatch('setEmojiPickerDisplayed', '');
        $event.stopPropagation();
        return;
      }
      if (this.$store.getters.getIsModalDisplayed) {
        this.$store.dispatch('setModal', {});
        $event.stopPropagation();
        return;
      }
      if (this.$store.getters.getDisplayVideoLinkPopup) {
        this.$store.dispatch('setDisplayVideoLinkPopup', false);
        $event.stopPropagation();
        return;
      }
      if (this.linkPopupIsActive) {
        this.$store.dispatch('setDisplayEditLinkPopup', false);
        $event.stopPropagation();
        return;
      }
      if (this.commentPopupVisible) {
        this.$store.dispatch('commentPopupVisible', false);
        $event.stopPropagation();
        return;
      }
      if (this.displayMembersDropdown) {
        this.$store.dispatch('setDisplayMembersDropdownId', '');
        $event.stopPropagation();
        return;
      }
      if (this.addMemberPopupIsOn) {
        if (this.$store.getters.getShowUpgradePlanPopup !== null) {
          this.$store.dispatch('setShowUpgradePlanPopup', null);
          return;
        }
        this.$store.dispatch('setAddMemberPopup', false);
        $event.stopPropagation();
        return;
      }

      if (this.$store.getters.displayInfoSidebar) {
        this.$store.dispatch('displayInfoSidebar', false);
        return;
      }
      if (this.isUploadImagePopupActive) {
        this.$store.dispatch('setImageUploadPopup', false);
        this.$store.dispatch('setCoverImageUploadPopup', false);
        $event.stopPropagation();
        return;
      }
      if (this.showInsertLinkPopup) {
        EventBus.$off('removeLinkMarkdown');
        this.$store.dispatch('setInsertLinkPopup', false);
        return;
      }

      if (this.$store.getters.getHelpPopupIsOn) {
        this.$store.dispatch('setHelpPopupIsOn', false);
        $event.stopPropagation();
        return;
      }

      if (this.isPage) {
        if (this.$store.getters.getCommentsAreActive) {
          this.$store.dispatch('setCommentsAreActive', false);
          this.$store.dispatch('setCurrentEditCommentID', '');
          return;
        }
        $event.stopPropagation();
        this.closePage();
      }
      if (this.isNewPage) {
        if (this.previousRouteName === 'page' && !this.isChildPage) {
          const route = this.closeBoardRoute;
          if (this.$store.getters.getCurrentRoute.path === route) return;
          this.$router.push(route);
        } else {
          this.closePage();
        }
        this.clearSidebarDraftPage();
      } else if (this.isBoard || this.isNewBoard || (this.isSprint && !this.isInbox)) {
        if (this.boardDropdownsOpened) {
          this.hideBoardDropdowns();
          return;
        }
        if (this.$store.getters.newFolderInput) {
          this.$store.dispatch('setNewFolderInput', false);
          return;
        }
        if (this.$store.getters.favouriteFolderForRename) {
          this.$store.dispatch('setFavouriteFolderForRename', '');
          return;
        }
        $event.stopPropagation();
        this.closeBoard();
      }

      if (this.$store.getters.getSprintForEditId?.id) {
        this.$store.dispatch('setSprintForEdit', {});
        return;
      }

      this.$store.dispatch('setCurrentCardIdForPopup', '');
    },
    closePage() {
      if (this.newPageCreating) {
        this.$store.dispatch('setNewPageCreating', false);
      }
      this.$store.dispatch('setLastSidebarItemAdded', {});
      if (this.closeArchived()) return;
      let route = this.closeBoardRoute;

      if (this.isChildPage) {
        route = `/${this.teamSlug}/page-${this.pageFromTree.parent}-${getDashedTitle(
          this.parentPage?.title
        )}`;
        this.$store.dispatch('setPage', this.parentPage);
        this.$store.dispatch('setCurrentPageID', this.parentPage?.id ?? this.pageFromTree.parent);
      }
      if (this.$store.getters.getCurrentRoute.path === route) return;
      this.$router.push(route);
    },
    closeBoard() {
      if (this.closeArchived()) return;
      if (this.isSprint) {
        this.$router.push(
          `/${this.teamSlug}/${this.$cons.projectSlug}-${this.currentProjectId}/sprints`
        );
        return;
      }
      this.$router.push(
        `/${this.teamSlug}/${this.$cons.projectSlug}-${this.currentProjectId}/boards`
      );
    },
    closeArchived() {
      if (
        this.previousRouteName === 'archived' &&
        (this.currentBoard?.archived || this.isPageArchived)
      ) {
        this.$router.push(this.getPreviousRoutePath);
        return true;
      }
      return false;
    },
    setBackgroundsActive() {
      this.$store.dispatch('setBoardsBackground', this.showBackgroundsForSelect ? '' : 'light');
    },
    findCurrentMembersById(id) {
      const index = this.currentMembers.findIndex((i) => i.user_id === id);
      return this.currentMembers[index];
    },
    joinProjectProcedure() {
      this.showJoinButton = false;
      const memberToAdd = {
        user_id: this.user.id,
        assigned_date: getCurrentDateInSeconds(),
      };
      const oldMembers = [...this.members];
      this.$store
        .dispatch('addMemberToProject', {
          membersToAdd: [memberToAdd],
          projectId: this.currentProjectId,
        })
        .then(() => {
          this.$store.dispatch('addUiNotification', {
            message: `${this.translate('joined')} ${this.currentProjectTitle} ${this.translate(
              'space'
            ).toLowerCase()}`,
            duration: 4000,
          });
        })
        .catch(() => {
          this.showJoinButton = true;
          this.$store.dispatch('setProjectMembers', oldMembers);
        });
    },
    closeInfoSidebar() {
      if (this.$store.getters.displayInfoSidebar) {
        this.$store.dispatch('displayInfoSidebar', false);
      }
    },
    toogleMemberDropdown(e) {
      if (this.isBoard) this.toggleBoardMembersDropdown();
      if (this.isProject) this.toggleProjectMembersDropdown();
      this.$store.dispatch('setHeaderMemberDropdownPosition', getElementPositionByPointer(e));
    },
    toggleBoardMembersDropdown() {
      if (this.showBoardMembersDropdown) {
        this.closeMembersDropdown();
        return;
      }
      this.$store.dispatch('setCurrentCardIdMemberDropdown', 'board');
      this.$store.dispatch('setCurrentCardMembersDropdown', this.currentBoard);
      this.$store.dispatch('setAddMembersTarget', {
        id: this.currentBoard.id,
        type: 'board',
      });
    },
    toggleProjectMembersDropdown() {
      if (this.showProjectMembersDropdown) {
        this.closeMembersDropdown();
        return;
      }

      this.closeInfoSidebar();
      this.$store.dispatch('setCurrentCardIdMemberDropdown', 'project');
      this.$store.dispatch('setCurrentCardMembersDropdown', this.currentProject);
      this.$store.dispatch('setAddMembersTarget', {
        id: this.currentProjectId,
        type: 'project',
      });
    },
    closeMembersDropdown() {
      this.$store.dispatch('setCurrentCardIdMemberDropdown', '');
      this.$store.dispatch('setCurrentCardMembersDropdown', {});
      this.$store.dispatch('setAddMembersTarget', {});
    },
    pushInfoNotification(message) {
      this.$store.dispatch('addUiNotification', {
        message,
        duration: 4000,
      });
    },
    setPagePrivate() {
      if (!this.page.is_public || this.page.archived) {
        return;
      }
      const isPublic = this.page.is_public;
      this.$store
        .dispatch('togglePagePublicFlag', {
          isPublic: false,
          pageId: this.page.id || '',
        })
        .then(() => {
          this.$store.dispatch('localUpdatePageProp', {
            pageId: this.page.id,
            propertyName: 'is_public',
            value: false,
          });
          if (isPublic === true) {
            this.pushInfoNotification(this.translate('pageIsNoLongerShared'));
          }
        });
    },
    togglePublicPage() {
      const isPublic = !this.page.is_public;
      const pageFromTree = this.$store.getters.getProjectPagesTree(this.page.project_id)[
        this.page.id
      ];

      if (!pageFromTree) return;

      this.$store.dispatch('localUpdatePageProp', {
        pageId: this.page.id,
        propertyName: 'is_public',
        value: isPublic,
      });

      if (pageFromTree.children && pageFromTree.children.length > 0) {
        localUpdatePagePropForAllChildren({
          pages: pageFromTree.children,
          projectId: this.currentProjectId,
          prop: 'is_public',
          value: isPublic,
        });
      }
      this.$store
        .dispatch('togglePagePublicFlag', {
          isPublic,
          pageId: this.page.id || '',
        })
        .then(({ data }) => {
          this.$store.dispatch('setPage', data.page);
          this.$store.dispatch('localUpdatePageProp', {
            pageId: this.page.id,
            propertyName: 'public_settings',
            value: data.page.public_settings,
          });
          const message = data.page.is_public
            ? this.translate('pageIsSharedToWeb')
            : this.translate('pageIsSetToPrivate');
          this.pushInfoNotification(message);
        })
        .catch(() => {
          this.$store.dispatch('localUpdatePageProp', {
            pageId: this.page.id,
            propertyName: 'is_public',
            value: !isPublic,
          });
          if (pageFromTree.children && pageFromTree.children.length > 0) {
            localUpdatePagePropForAllChildren({
              pages: pageFromTree.children,
              projectId: this.currentProjectId,
              prop: 'is_public',
              value: isPublic,
            });
          }
        });
    },
    togglePageIndex() {
      const index = ['index', 'follow'];
      const noIndex = ['none'];
      const page = { public_settings: { robots: this.pageIndex ? noIndex : index } };

      this.$store
        .dispatch('updatePageProps', {
          page,
          pageId: this.page.id,
        })
        .then(({ data }) => {
          this.$store.dispatch('setPage', data.page);
          const message = this.pageIndex
            ? this.translate('searchIndexingEnabled')
            : this.translate('searchIndexingDisabled');
          this.pushInfoNotification(message);
        });
    },

    updatePage({ pageId, payload }) {
      return this.$store.dispatch('debounceUpdatePage', {
        pageId,
        payload,
      });
    },

    sideSectionsResize() {
      EventBus.$emit('sidebarWidthDecreasing');
      EventBus.$emit('checkBreadcrumbsTitleOverflow');
    },

    sortMembersByAssignedDate(members) {
      const projectMembers = this.$store.getters.getTeamMembers;
      const inactiveMembers = this.$store.getters.getInactiveTeamMembers;
      return members
        ? [...members].sort((a, b) => {
            if (a.assigned_date !== b.assigned_date) {
              return a.assigned_date - b.assigned_date;
            }
            const aMember = projectMembers[a.user_id] || inactiveMembers[a.user_id];
            const bMember = projectMembers[b.user_id] || inactiveMembers[b.user_id];
            if (!aMember || !bMember) {
              return a.user_id?.localeCompare(b.user_id);
            }
            if (aMember.display_name !== bMember.display_name) {
              return aMember.display_name.localeCompare(bMember.display_name);
            }
            return aMember.email.localeCompare(bMember.email);
          })
        : members;
    },

    filterInactiveMembers(members = []) {
      return members.filter((member) => !this.inactiveMembers[member.user_id]);
    },

    clearSidebarDraftPage() {
      this.$store.dispatch('localRemovePageInTree', {
        projectId: this.sidebarDraftPage.projectId,
        pageId: this.sidebarDraftPage.id,
      });
      this.$store.dispatch('setSidebarPageDraft', {});
    },

    updateAttribute(attr) {
      this.$emit('updateAttribute', attr);
    },

    renameSectionTitle() {
      this.$store.dispatch('setSettingsCurrentBoardId', this.board.id);
      this.$router.push({
        name: this.$constants.settingsBoardsRouteName,
        params: { projectIdParam: this.projectId, willFocusInput: true },
      });
    },

    addEventListeners() {
      if (this.listenersAdded) return;
      window.addEventListener('keydown', this.handleClose);
      this.listenersAdded = true;
    },

    removeEventListeners() {
      if (!this.listenersAdded) return;
      window.removeEventListener('keydown', this.handleClose);
      this.listenersAdded = false;
    },

    hideSprintHeaderElement(evt) {
      if (!evt[0] || !evt[0].contentRect) return;
      const {
        contentRect: { width },
      } = evt[0];

      // 32 is header left + right padding
      const leftRightWidth = this.headerLeftSideWidth + this.headerRightSideWidth + 32;

      this.showSprintsDates = width - leftRightWidth > this.sprintDatesWidth;
    },

    async handleTableOfContentsAndSubpagesToggle(item) {
      const propName =
        item.id === 'hideTableOfContents' ? 'hide_table_of_contents' : 'hide_subpages';

      this.$store.dispatch('localUpdatePageProp', {
        pageId: this.page.id,
        propertyName: propName,
        value: !this.page[propName],
      });

      try {
        await this.$store.dispatch('updatePageProps', {
          page: {
            [propName]: this.page[propName],
          },
          pageId: this.page.id,
        });
      } catch {
        this.$store.dispatch('localUpdatePageProp', {
          pageId: this.page.id,
          propertyName: propName,
          value: !this.page[propName],
        });
      }
    },
  },
};
</script>

<style lang="scss">
.header-wrap {
  user-select: none;
  font-family: $primaryFontFamily;
  max-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  z-index: 5;

  .header-inner {
    display: flex;
    justify-content: space-between;
    width: 100%;
    box-sizing: border-box;
    top: 0;
    right: 0;
    z-index: 2;
    height: $headerHeight;
    position: relative;
    border-bottom: 1px solid;
    @apply px-4
    bg-background-strongest
    border-b-dividers-default;

    &.page-header {
      align-items: center;
      .options {
        margin-left: auto;
      }
    }

    .header-column {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      .join-project-button {
        width: $joinProjectButtonWidth;
        height: 22px;
        border-radius: 16px;
        @apply ml-2 electron-no-drag;

        .button-label {
          font-size: 12px;
          line-height: 18px;
          font-weight: 500;
        }
      }
      .members-list-header-wrap {
        height: 32px;
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        box-sizing: border-box;
        border-radius: 4px;
        @apply mr-2;

        .members-list-header {
          position: relative;
          display: flex;
          align-items: center;
          border-right: none;
          height: 100%;
          @apply px-2 rounded cursor-pointer;

          &:hover {
            @apply bg-icon-bg;
          }

          .member-avatar {
            border-width: 1px;
          }

          .rest-member-wrap {
            margin-right: 0px;
          }
        }

        .add-member-icon-wrap {
          @apply flex items-center h-full px-2 rounded hover:bg-icon-bg cursor-pointer;

          .add-member-icon-content {
            border-radius: 50%;
            height: 24px;
            width: 24px;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 1px solid;
            @apply border-important-border bg-important-bg box-border hover:bg-important-hover;
          }
        }
        &.active {
          @apply bg-hover-default;
          .add-member-icon {
            @apply bg-icon-hover;
          }
        }
        &.right-spacing {
          margin-right: 58px;
        }
      }

      .settings-button-popover {
        @apply ml-2
        mr-0;

        &.disabled-item {
          em {
            @apply bg-inactive-icon;
          }

          &:hover:not(.mobile *) {
            background-color: transparent;
          }
        }
      }

      .options-buttons-wrap {
        display: flex;
        align-items: center;
        justify-content: center;

        &.left-border {
          border-left: 1px solid;
          @apply border-dividers-default;
        }

        .header-icon {
          @apply mr-0
          ml-2;
        }

        .settings-button-popover {
          .trigger {
            .settings-button-wrap {
              .header-icon {
                @apply ml-0;
              }
            }
          }
        }
      }

      .esc-button {
        @apply w-8
        ml-2;

        .square-icon-wrap {
          @apply m-0;
        }
      }
    }
  }
  .project-icon-small-wrap {
    @apply bg-background-strongest;
  }

  .copy-link-button {
    @apply z-[1] bg-background-strongest;
  }
}

.right-side-content {
  .header-wrap {
    .header-inner {
      &.sidebar-expanded {
        padding-left: 16px;
      }
    }
  }
}
.outline-none {
  outline: none;
}

.mobile {
  .header-inner {
    box-sizing: border-box;
    line-height: 20px;
    height: 48px;
    @apply px-2;

    .header-column {
      .esc-button {
        @apply w-5
        h-5
        mr-2
        ml-0;
      }
    }

    .options {
      margin-left: 8px;

      .settings-button-popover {
        margin-left: 8px;
      }

      .options-buttons-wrap {
        @apply pl-0;
      }

      .members-list-header-wrap {
        @apply mr-2;

        &.right-spacing {
          @apply mr-2;
        }

        .add-member-icon-wrap {
          @apply m-0;

          .add-member-icon-content {
            @apply text-gray-500
              rounded-xl
              w-12;

            .add-member-icon {
              @apply bg-icon-default
                m-0;
            }
          }
        }

        .members-count {
          @apply flex
            justify-center
            text-xs
            leading-4
            text-text-default
            min-w-[16px];
        }
      }
    }

    .sidebar-menu-button {
      min-width: 32px;

      .sidebar-icon {
        margin-right: 0px;
      }
    }
  }
}
</style>
